/* eslint-disable max-lines-per-function */
import './CoralContent.scss'

import { useIsWindowBiggerThan } from '@bob/library'
import { Asset } from '@websrc/App/Components/Asset/Asset'
import { Baseplate, StudRange } from '@websrc/App/Components/Baseplate/Baseplate'
import { BrickColor } from '@websrc/App/Components/Brick/Brick'
import { Markdown } from '@websrc/App/Components/Markdown/Markdown'
import { mapCoralWebContent } from '@websrc/DataMappers/mapSpot'
import clsx from 'clsx'
import React, { FC, useMemo } from 'react'

type CoralContentProps = ReturnType<typeof mapCoralWebContent>

export const CoralContent: FC<CoralContentProps> = ({
  asset,
  headline,
  description,
  color_variant,
  hide_baseplate,
  direction,
}) => {
  const isTablet = useIsWindowBiggerThan(719)
  const isCustomTablet = useIsWindowBiggerThan(949)
  const isDesktop = useIsWindowBiggerThan(1079)
  const isWideScreen = useIsWindowBiggerThan(1439)

  const primaryBrickColor: BrickColor = useMemo(() => {
    switch (color_variant) {
      case 'coral':
        return BrickColor.EarthBlue
      case 'sand':
        return BrickColor.Teal
      case 'warm-yellow':
        return BrickColor.Coral
      default:
        return BrickColor.Sand
    }
  }, [color_variant])

  const secondaryBrickColor: BrickColor = useMemo(() => {
    switch (color_variant) {
      case 'coral':
        return BrickColor.Sand
      case 'sand':
        return BrickColor.WarmYellow
      case 'warm-yellow':
        return BrickColor.Teal
      default:
        return BrickColor.Coral
    }
  }, [color_variant])

  const studRange = useMemo((): StudRange[] => {
    if (isWideScreen) {
      return [
        {
          snapPosition: 'top-left',
          size: { rows: 13, columns: 7 },
          offset: { top: -2, left: 3 },
          bricks:
            direction === 'left'
              ? [{ row: 7, column: 0, color: primaryBrickColor, snapPosition: 'bottom-left' }]
              : [
                  { row: 11, column: 0, color: primaryBrickColor, snapPosition: 'bottom-left' },
                  { row: 13, column: -1, color: secondaryBrickColor, snapPosition: 'bottom-left' },
                ],
        },
        {
          snapPosition: 'top-right',
          size: { rows: 13, columns: 7 },
          offset: { top: -2, right: 3 },
          bricks:
            direction === 'left'
              ? [
                  { row: 10, column: 9, color: primaryBrickColor, snapPosition: 'top-right' },
                  { row: 12, column: 8, color: secondaryBrickColor, snapPosition: 'top-right' },
                ]
              : [{ row: 4, column: 8, color: primaryBrickColor, snapPosition: 'top-right' }],
        },
      ]
    }
    if (isDesktop) {
      return [
        {
          snapPosition: 'top-left',
          size: { rows: 13, columns: 5 },
          offset: { top: -2, left: 3 },
          bricks:
            direction === 'left'
              ? [{ row: 7, column: 0, color: primaryBrickColor, snapPosition: 'bottom-left' }]
              : [
                  { row: 11, column: 0, color: primaryBrickColor, snapPosition: 'bottom-left' },
                  { row: 13, column: -1, color: secondaryBrickColor, snapPosition: 'bottom-left' },
                ],
        },
        {
          snapPosition: 'top-right',
          size: { rows: 13, columns: 5 },
          offset: { right: 3 },
          bricks:
            direction === 'left'
              ? [
                  { row: 10, column: 7, color: primaryBrickColor, snapPosition: 'top-right' },
                  { row: 12, column: 6, color: secondaryBrickColor, snapPosition: 'top-right' },
                ]
              : [{ row: 4, column: 6, color: primaryBrickColor, snapPosition: 'top-right' }],
        },
      ]
    } else if (isCustomTablet) {
      return [
        {
          snapPosition: 'top-left',
          size: { rows: 12, columns: 3 },
          offset: { top: -3, left: 3 },
          bricks:
            direction === 'left'
              ? [{ row: 7, column: 0, color: primaryBrickColor, snapPosition: 'bottom-left' }]
              : [
                  { row: 12, column: 0, color: primaryBrickColor, snapPosition: 'bottom-left' },
                  { row: 14, column: -1, color: secondaryBrickColor, snapPosition: 'bottom-left' },
                ],
        },
        {
          snapPosition: 'top-right',
          size: { rows: 12, columns: 3 },
          offset: { top: -2, right: 3 },
          bricks:
            direction === 'left'
              ? [
                  { row: 11, column: 5, color: primaryBrickColor, snapPosition: 'top-right' },
                  { row: 13, column: 4, color: secondaryBrickColor, snapPosition: 'top-right' },
                ]
              : [{ row: 4, column: 4, color: primaryBrickColor, snapPosition: 'top-right' }],
        },
      ]
    } else if (isTablet && !isCustomTablet) {
      return [
        {
          snapPosition: 'top-left',
          size: { rows: 8, columns: 3 },
          offset: { top: -1, left: 3 },
          bricks:
            direction === 'left'
              ? [{ row: 5, column: 0, color: primaryBrickColor, snapPosition: 'bottom-left' }]
              : [{ row: 10, column: 0, color: secondaryBrickColor, snapPosition: 'bottom-left' }],
        },
        {
          snapPosition: 'top-right',
          size: { rows: 8, columns: 3 },
          offset: { top: 1, right: 3 },
          bricks:
            direction === 'left'
              ? [{ row: 9, column: 4, color: secondaryBrickColor, snapPosition: 'top-right' }]
              : [{ row: 4, column: 4, color: primaryBrickColor, snapPosition: 'top-right' }],
        },
      ]
    } else {
      return [
        {
          snapPosition: 'top-left',
          size: { rows: 4, columns: 1 },
          offset: { top: 4 },
        },
        {
          snapPosition: 'top-right',
          size: { rows: 4, columns: 1 },
          offset: { top: 6 },
        },
      ]
    }
  }, [
    direction,
    isCustomTablet,
    isDesktop,
    isTablet,
    isWideScreen,
    primaryBrickColor,
    secondaryBrickColor,
  ])

  return (
    <div className={'coral-content-container'}>
      <div className={clsx('coral-content', `m--${direction}`)}>
        {asset?.thumbnail && (
          <Asset
            altText={asset.altText}
            className={clsx('coral-content__asset', `m--${direction}`)}
            objectFit={'cover'}
            thumbnail={asset.thumbnail}
          />
        )}
        <Baseplate
          className={clsx('coral-content__background', `m--${color_variant}`)}
          studRanges={hide_baseplate ? [] : studRange}
        >
          <div />
        </Baseplate>
        <div className={clsx('coral-content__wrapper', `m--${color_variant}`, `m--${direction}`)}>
          <h3 className={clsx('coral-content__headline', `m--${direction}`)}>{headline}</h3>
          {description && (
            <Markdown
              className={clsx('coral-content__description', `m--${direction}`)}
              markdown={description}
            />
          )}
        </div>
      </div>
    </div>
  )
}
